import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import { getItem } from '../../../utils/localStorage';
import { makeRequest2 } from '../../../utils/makeRequest';
import { GET_DEPARTMENT_URL, getUsersURL, updateUserURL } from '../../../utils/urls';

const UsersTab = () => {
    const [users, setUsers] = useState([]);
    const [editModal, setEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [department, setDepartment] = useState('');
    const [departments, setDepartments] = useState([]);
    const [userId, setUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [selectedModule, setSelectedModule] = useState('');
    const [editRole, setEditRole] = useState('Staff');
    const toast = useRef(null);

    const modules = [
        { name: 'visitorAccess', displayName: 'Visitor', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'levy', displayName: 'Levy', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'maintenance', displayName: 'Maintenance', permissions: ['create', 'read', 'update', 'delete', 'approve'] },
        { name: 'lease', displayName: 'Lease', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'vas', displayName: 'VAS', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'tickets', displayName: 'Tickets', permissions: ['create', 'read', 'update', 'delete', 'review'] },
        { name: 'utility', displayName: 'Utility', permissions: ['create', 'read', 'update', 'delete', 'approve'] },
        { name: 'booking', displayName: 'Booking', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'handover', displayName: 'Handover', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'accounts', displayName: 'Expense', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'campaigns', displayName: 'Campaign', permissions: ['create', 'read', 'update', 'delete'] },
        { name: 'procurement', displayName: 'Procurement', permissions: ['create', 'read', 'update', 'delete', 'approve'] },
        { name: 'propertyManagement', displayName: 'PropertyManagement', permissions: ['create', 'read', 'update', 'delete', 'approve'] }
    ];

    const fetchUsers = async () => {
        const facilityId = await getItem('selectedFacilityId');
        setLoading(true);

        try {
            const response = await makeRequest2(`${getUsersURL}/${facilityId}`, 'GET');

            if (response.success) {
                const usersData = Array.isArray(response.data) ? response.data : [];
                setUsers(usersData);
            } else {
                console.error('Failed to fetch users:', response.error);
                setUsers([]);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.error || 'Failed to fetch users'
                });
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            setUsers([]);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error fetching users: ' + err.message
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${GET_DEPARTMENT_URL}/${facilityId}`, 'GET');

            if (response.success && response.data) {
                // Handle nested data structure like DepartmentTab does
                const departmentData = response.data.data || response.data;
                const departmentsArray = Array.isArray(departmentData)
                    ? departmentData
                    : [departmentData];
                setDepartments(departmentsArray);
            } else {
                setDepartments([]);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
            setDepartments([]);
        }
    };

    const handleEdit = (user) => {
        setSelectedUser(user);

        if (user.fullName && (!user.firstName || !user.lastName)) {
            const nameParts = user.fullName.split(' ');
            const lastName = nameParts.length > 1 ? nameParts.pop() : '';
            const firstName = nameParts.join(' ');

            setFirstName(firstName);
            setLastName(lastName);
        } else {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
        }

        setPhoneNumber(user.phoneNumber || '');
        setEmail(user.email || '');

        // Set department ID properly - handle both populated and non-populated cases
        const departmentId = user.department?._id || user.department || '';
        setDepartment(departmentId);

        setPermissions(user.permissions || {});
        setEditRole(user.role || 'Staff');
        setUserId(user._id);
        setEditModal(true);
    };

    const handlePermissionChange = (module, permission, checked) => {
        setPermissions(prev => ({
            ...prev,
            [module]: {
                ...(prev[module] || {}),
                [permission]: checked
            }
        }));
    };

    const selectAllPermissions = (module, checked) => {
        const modulePermissions = {};
        const permissionsList = modules.find(m => m.name === module).permissions;

        permissionsList.forEach(permission => {
            modulePermissions[permission] = checked;
        });

        setPermissions(prev => ({
            ...prev,
            [module]: modulePermissions
        }));
    };

    const handleClearForm = () => {
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
        setEmail('');
        setDepartment('');
        setPermissions({});
        setEditRole('Staff');
        setSelectedModule('');
    };

    const handleUpdateUser = async () => {
        const facilityId = await getItem('selectedFacilityId');

        try {
            const userData = {
                firstName,
                lastName,
                phoneNumber,
                email,
                department: department || null,
                permissions,
                role: editRole,
            };

            const response = await makeRequest2(`${updateUserURL}/${facilityId}/${userId}`, 'POST', userData);

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'User updated successfully'
                });
                setEditModal(false);
                fetchUsers();
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err.message
            });
        }
    };

    const clearFilters = () => {
        setSearchKeyword('');
        setDepartmentFilter('');
    };

    const closeModal = () => {
        setEditModal(false);
        setSelectedUser(null);
        handleClearForm();
    };

    const nameTemplate = (rowData) => {
        return (
            <div>
                {rowData.fullName || 'N/A'}
            </div>
        );
    };

    const departmentTemplate = (rowData) => {
        return (
            <div>
                {rowData.department ? (
                    <span
                        className="px-2 py-1 border-round text-white font-bold"
                        style={{ backgroundColor: '#17a2b8', fontSize: '0.75rem' }}
                    >
                        {rowData.department.name}
                    </span>
                ) : (
                    <span className="text-500">No Department</span>
                )}
            </div>
        );
    };

    const permissionsTemplate = (rowData) => {
        const permissions = rowData.permissions;
        if (!permissions || typeof permissions !== 'object') {
            return <span className="text-500">No permissions</span>;
        }

        const activeModules = Object.keys(permissions).filter(module => {
            const modulePermissions = permissions[module];
            return Object.values(modulePermissions).some(perm => perm === true);
        });

        if (activeModules.length === 0) {
            return <span className="text-500">No permissions</span>;
        }

        const menuItems = activeModules.map(module => {
            const modulePermissions = permissions[module];
            const activePermissions = Object.keys(modulePermissions).filter(
                perm => modulePermissions[perm] === true
            );

            return {
                label: (
                    <div style={{ padding: '8px 12px', minWidth: '250px' }}>
                        <div className="font-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                            {module}
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {activePermissions.map(permission => (
                                <span
                                    key={permission}
                                    className="px-2 py-1 border-round text-white font-bold"
                                    style={{
                                        backgroundColor: '#28a745',
                                        fontSize: '0.7rem',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {permission}
                                </span>
                            ))}
                        </div>
                    </div>
                ),
                command: () => { }
            };
        });

        return (
            <div>
                <Menu
                    model={menuItems}
                    popup
                    ref={(el) => el && (rowData.permissionsMenu = el)}
                    id={`permissions_menu_${rowData._id}`}
                    style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        minWidth: '280px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                    className="custom-permissions-menu"
                />
                <Button
                    label={`${activeModules.length} Module${activeModules.length > 1 ? 's' : ''}`}
                    icon="pi pi-chevron-down"
                    className="p-button-sm p-button-outlined p-button-secondary"
                    onClick={(e) => {
                        if (rowData.permissionsMenu) {
                            rowData.permissionsMenu.toggle(e);
                        }
                    }}
                />
            </div>
        );
    };

    const actionsTemplate = (rowData) => {
        return (
            <Button
                icon="ti ti-pencil"
                className="p-button-rounded p-button-warning p-button-sm"
                tooltip="Edit User"
                onClick={() => handleEdit(rowData)}
            />
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Filter users based on search and filters
    const filteredUsers = users.filter((user) => {
        const searchableText = [
            user.fullName,
            user.firstName,
            user.lastName,
            user.email,
            user.phoneNumber,
            user.department?.name
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

        const matchesSearch = searchKeyword ?
            searchableText.includes(searchKeyword.toLowerCase()) :
            true;

        const matchesDepartment = departmentFilter ?
            user.department?._id === departmentFilter :
            true;

        return matchesSearch && matchesDepartment;
    });

    useEffect(() => {
        fetchUsers();
        fetchDepartments();
    }, []);

    return (
        <div className="card">
            <div className="card-header">
                <h5>Users Management</h5>
            </div>
            <div className="card-body">
                <Toast ref={toast} />

                {/* Filters */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <input
                            className="form-control"
                            placeholder="Search users..."
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-control"
                            value={departmentFilter}
                            onChange={(e) => setDepartmentFilter(e.target.value)}
                        >
                            <option value="">All Departments</option>
                            {Array.isArray(departments) && departments.map(dept => (
                                <option key={dept._id} value={dept._id}>
                                    {dept.name}
                                </option>
                            ))}
                        </select>
                    </div>


                </div>

                <DataTable
                    value={filteredUsers}
                    loading={loading}
                    paginator
                    rows={10}
                    emptyMessage="No users found"
                    className="p-datatable-sm"
                    stripedRows
                    sortField="fullName"
                    sortOrder={1}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} users"
                    scrollable
                    scrollHeight="400px"
                >
                    <Column
                        header="#"
                        body={(rowData, { rowIndex }) => rowIndex + 1}
                        style={{ width: '50px' }}
                    />
                    <Column
                        field="fullName"
                        header="Name"
                        body={nameTemplate}
                        sortable
                        style={{ minWidth: '180px' }}
                    />
                    <Column
                        field="email"
                        header="Email"
                        sortable
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        field="phoneNumber"
                        header="Phone"
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        field="department"
                        header="Department"
                        body={departmentTemplate}
                        style={{ minWidth: '130px' }}
                    />
                    <Column
                        field="role"
                        header="Role"
                        body={(rowData) => (
                            <span className={`badge ${rowData.role === 'ninja' ? 'bg-dark' : rowData.role === 'admin' ? 'bg-primary' : 'bg-secondary'}`}>
                                {rowData.role || 'Staff'}
                            </span>
                        )}
                        style={{ minWidth: '100px' }}
                    />
                    <Column
                        header="Permissions"
                        body={permissionsTemplate}
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        field="createdAt"
                        header="Created"
                        body={(rowData) => formatDate(rowData.createdAt)}
                        sortable
                        style={{ minWidth: '100px' }}
                    />
                    <Column
                        header="Actions"
                        body={actionsTemplate}
                        style={{ width: '80px' }}
                    />
                </DataTable>
            </div>

            <Dialog
                header="Edit User"
                visible={editModal}
                onHide={closeModal}
                style={{ width: '80vw', maxWidth: '900px' }}
                modal
                className="p-fluid"
            >
                {selectedUser && (
                    <div>
                        <div className="grid mb-4">
                            <div className="col-12 md:col-6">
                                <label className="block text-900 font-medium mb-2">First Name</label>
                                <InputText
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="block text-900 font-medium mb-2">Last Name</label>
                                <InputText
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="block text-900 font-medium mb-2">Phone Number</label>
                                <InputText
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="block text-900 font-medium mb-2">Email</label>
                                <InputText
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="block text-900 font-medium mb-2">Department</label>
                                <Dropdown
                                    value={department}
                                    options={[
                                        { label: 'Select Department (Optional)', value: '' },
                                        ...(Array.isArray(departments) ? departments.map(dept => ({
                                            label: dept.name,
                                            value: dept._id
                                        })) : [])
                                    ]}
                                    onChange={(e) => {
                                        console.log('Department selected:', e.value);
                                        setDepartment(e.value);
                                    }}
                                    placeholder="Select Department (Optional)"
                                    optionLabel="label"
                                    optionValue="value"
                                />
                                {(!Array.isArray(departments) || departments.length === 0) && (
                                    <small className="text-500 mt-1 block">
                                        No departments available. Please create departments first.
                                    </small>
                                )}
                                {Array.isArray(departments) && departments.length > 0 && (
                                    <small className="text-500 mt-1 block">
                                        {departments.length} department{departments.length > 1 ? 's' : ''} available
                                    </small>
                                )}
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="block text-900 font-medium mb-2">Role</label>
                                <Dropdown
                                    value={editRole}
                                    options={[
                                        { label: 'Staff', value: 'Staff' },
                                        { label: 'Admin', value: 'admin' },
                                        { label: 'Editor', value: 'editor' },
                                        { label: 'User', value: 'user' },
                                        { label: 'Ninja (Full Access)', value: 'ninja' },
                                    ]}
                                    onChange={(e) => setEditRole(e.value)}
                                    placeholder="Select Role"
                                />
                            </div>
                        </div>

                        <div className="mt-4">
                            <div className="flex align-items-center mb-3">
                                <i className="pi pi-check-square text-primary mr-2" style={{ fontSize: '1.2rem' }}></i>
                                <h6 className="mb-0 font-bold">Roles & Permissions</h6>
                            </div>

                            {/* Module Selection Dropdown */}
                            <div className="mb-3">
                                <label className="block text-900 font-medium mb-2">Select Module to Configure</label>
                                <Dropdown
                                    value={selectedModule}
                                    options={[
                                        { label: 'Select a module', value: '' },
                                        ...modules.map(module => ({ label: module.name, value: module.name }))
                                    ]}
                                    onChange={(e) => setSelectedModule(e.value)}
                                    placeholder="Choose a module to configure permissions"
                                    className="w-full"
                                    style={{ height: '45px' }}
                                />
                            </div>

                            {/* Show permissions for selected module */}
                            {selectedModule && (
                                <div className="mt-3 p-3 border-1 border-300 border-round">
                                    <div className="flex align-items-center justify-content-between mb-3">
                                        <h6 className="mb-0 font-bold text-primary">{selectedModule} Permissions</h6>
                                        <div className="flex gap-2">
                                            <Button
                                                label="Select All"
                                                onClick={() => selectAllPermissions(selectedModule, true)}
                                                className="p-button-outlined p-button-primary p-button-sm"
                                                size="small"
                                            />
                                            <Button
                                                label="Clear All"
                                                onClick={() => selectAllPermissions(selectedModule, false)}
                                                className="p-button-outlined p-button-secondary p-button-sm"
                                                size="small"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid">
                                        {modules.find(m => m.name === selectedModule)?.permissions.map(permission => (
                                            <div key={permission} className="col-12 md:col-6 lg:col-4">
                                                <div className="flex align-items-center">
                                                    <input
                                                        type="checkbox"
                                                        id={`${selectedModule}-${permission}`}
                                                        checked={permissions[selectedModule]?.[permission] || false}
                                                        onChange={(e) => handlePermissionChange(selectedModule, permission, e.target.checked)}
                                                        className="mr-2"
                                                    />
                                                    <label
                                                        htmlFor={`${selectedModule}-${permission}`}
                                                        className="text-900 font-medium cursor-pointer"
                                                        style={{ textTransform: 'capitalize' }}
                                                    >
                                                        {permission}
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Show summary of all permissions */}
                            <div className="mt-4">
                                <h6 className="mb-2 font-bold">Current Permissions Summary</h6>
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }} className="border-1 border-300 border-round p-2">
                                    {Object.keys(permissions).length === 0 ? (
                                        <p className="text-500 text-center">No permissions assigned yet</p>
                                    ) : (
                                        Object.keys(permissions).map(module => {
                                            const modulePermissions = permissions[module];
                                            const activePermissions = Object.keys(modulePermissions).filter(
                                                perm => modulePermissions[perm] === true
                                            );

                                            if (activePermissions.length === 0) return null;

                                            return (
                                                <div key={module} className="mb-2 p-2 surface-100 border-round">
                                                    <div className="font-bold text-primary mb-1">{module}</div>
                                                    <div className="flex flex-wrap gap-1">
                                                        {activePermissions.map(permission => (
                                                            <span
                                                                key={permission}
                                                                className="px-2 py-1 border-round text-white font-bold"
                                                                style={{ backgroundColor: '#28a745', fontSize: '0.7rem' }}
                                                            >
                                                                {permission}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-content-end gap-2 mt-4">
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                onClick={closeModal}
                                className="p-button-outlined p-button-secondary"
                            />
                            <Button
                                label="Update"
                                icon="pi pi-check"
                                onClick={handleUpdateUser}
                                className="p-button-primary"
                            />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default UsersTab;